import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink, Loader2, Heart, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useCartStore } from "@/stores/cartStore";
import { useFavoritesStore } from "@/stores/favoritesStore";
import { useCurrencyStore, formatConvertedPrice } from "@/stores/currencyStore";

const FREE_SHIP_THRESHOLD = 150;

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } = useCartStore();
  const favoriteIds = useFavoritesStore(s => s.ids);
  const toggleFavorite = useFavoritesStore(s => s.toggle);
  const displayCurrency = useCurrencyStore(s => s.code);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
  const baseCurrency = items[0]?.price.currencyCode || "USD";
  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - totalPrice);
  const progress = Math.min(100, (totalPrice / FREE_SHIP_THRESHOLD) * 100);

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  // Drop variantIds from `removing` once the store no longer contains them.
  // Prevents a stale id from hiding a newly-re-added item.
  useEffect(() => {
    setRemoving((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(items.map((i) => i.variantId));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (present.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [items]);

  const handleRemove = (variantId: string) => {
    if (removing.has(variantId)) return;
    setRemoving((s) => new Set(s).add(variantId));
    // Wait for fade-out to finish, then remove from store. Keep the
    // variantId in `removing` so the item stays hidden until the store
    // actually drops it from `items` — prevents a flash of the item
    // re-appearing after the animation completes.
    setTimeout(() => {
      removeItem(variantId);
    }, 350);
  };

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
      setIsOpen(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={`Cart${totalItems > 0 ? ` (${totalItems} item${totalItems !== 1 ? "s" : ""})` : ""}`}
          className="relative inline-flex items-center justify-center hover:text-accent transition-colors"
        >
          <ShoppingBag className="w-5 h-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-accent text-accent-foreground">
              {totalItems}
            </Badge>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-2xl">YOUR CART</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''} in your cart`}
          </SheetDescription>
          <Link
            to="/favourites"
            onClick={() => setIsOpen(false)}
            className="inline-flex items-center gap-2 mt-2 font-display text-xs tracking-widest text-muted-foreground hover:text-accent transition-colors w-fit"
          >
            <Heart className={`w-3.5 h-3.5 ${favoriteIds.length > 0 ? "fill-accent text-accent" : ""}`} />
            VIEW FAVOURITES{favoriteIds.length > 0 ? ` (${favoriteIds.length})` : ""}
          </Link>
        </SheetHeader>
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length > 0 && (
            <div className="flex-shrink-0 mb-4 p-3 border border-border bg-secondary/40">
              <div className="flex items-center gap-2 text-xs font-display tracking-widest mb-2">
                <Truck className="w-4 h-4 text-accent" />
                {remaining > 0 ? (
                  <span>ADD <span className="text-accent">{formatConvertedPrice(remaining, baseCurrency, displayCurrency)}</span> FOR FREE SHIPPING</span>
                ) : (
                  <span className="text-accent">YOU QUALIFY FOR FREE SHIPPING ✓</span>
                )}
              </div>
              <div className="h-1.5 bg-background overflow-hidden">
                <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className={`flex gap-4 p-2 ${removing.has(item.variantId) ? "cart-item-removing" : ""}`}>
                      <div className="w-16 h-16 bg-secondary/20 rounded-md overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img src={item.product.node.images.edges[0].node.url} alt={item.product.node.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium truncate">{item.product.node.title}</h4>
                          {favoriteIds.includes(item.product.node.id) && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-accent font-display shrink-0">
                              <Heart className="w-3 h-3 fill-accent text-accent" />
                              Favourited
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{item.selectedOptions.map(o => o.value).join(' • ')}</p>
                        <p className="font-semibold">{formatConvertedPrice(item.price.amount, item.price.currencyCode, displayCurrency)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label={favoriteIds.includes(item.product.node.id) ? "Remove from favourites" : "Add to favourites"}
                            aria-pressed={favoriteIds.includes(item.product.node.id)}
                            onClick={() => toggleFavorite(item.product.node.id)}
                          >
                            <Heart className={`h-3 w-3 ${favoriteIds.includes(item.product.node.id) ? "fill-accent text-accent" : ""}`} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Remove" onClick={() => handleRemove(item.variantId)} disabled={removing.has(item.variantId)}>
                            <Trash2 className="h-3 w-3 trash-icon" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.variantId, item.quantity - 1)}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.variantId, item.quantity + 1)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 space-y-4 pt-4 border-t bg-background">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-xl font-bold">{formatConvertedPrice(totalPrice, baseCurrency, displayCurrency)}</span>
                </div>
                <Button
                  onClick={handleCheckout}
                  className="w-full rounded-full font-display tracking-[0.2em] text-base h-12 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-all duration-300 active:scale-[0.97] shadow-md hover:shadow-lg btn-shine"
                  size="lg"
                  disabled={items.length === 0 || isLoading || isSyncing}
                >
                  {isLoading || isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : "CHECKOUT"}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};